import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimPriceLevelEnum } from 'gql/graphql';
import { totalsByConstructionTypeKey } from 'pages/Project/BimProject/WorkOrder/types';

import { useEffect, useMemo } from 'react';

import { bimWorkOrderVersionTotalsRow } from '../../workOrderVersionQueries';

export const useBimWorkOrderTotalsVersionTable = ({
    bimTotalsChapter,
    workOrderVersionId,
    constructionTypeId,
    priceLevel,
}: {
    bimTotalsChapter: string;
    workOrderVersionId: number;
    constructionTypeId: number | undefined;
    priceLevel: BimPriceLevelEnum | undefined;
}) => {
    const isTotalsByConstructionType = useMemo(
        () => bimTotalsChapter === totalsByConstructionTypeKey,
        [bimTotalsChapter],
    );

    const totalVariables = useMemo(() => {
        if (isTotalsByConstructionType) {
            return {
                workOrderVersionId,
                constructionTypeId,
                useInPrices: false,
                priceLevel,
            };
        }

        return {
            workOrderVersionId,
            useInPrices: false,
            priceLevel,
        };
    }, [constructionTypeId, isTotalsByConstructionType, priceLevel, workOrderVersionId]);

    const isRequestLegit = useMemo(() => {
        if (isTotalsByConstructionType) {
            return !!(workOrderVersionId && constructionTypeId);
        }

        if (!isTotalsByConstructionType) {
            return !!workOrderVersionId;
        }

        return false;
    }, [constructionTypeId, isTotalsByConstructionType, workOrderVersionId]);

    const requestId = `bimWorkOrderTotalsTable${isTotalsByConstructionType ? '_ByConstructionType' : ''}`;

    const [makeReset] = useResetStoreData({
        requestId,
    });

    useEffect(
        () => () => {
            makeReset(undefined);
        },
        [makeReset, totalVariables],
    );

    const [rowTotalsData, rowTotalsDataStatus] = useRequest({
        requestId,
        query: bimWorkOrderVersionTotalsRow,
        variables: totalVariables,
        withEffect: isRequestLegit,
    });

    const isLoading = rowTotalsDataStatus === RequestStatus.FETCHING;

    return {
        tableData: rowTotalsData?.bimWorkOrderVersionTotals?.items,
        isLoading,
    };
};
